import { createLogger } from '@/core/helpers/logger.helper';
import type { DuelApi } from '@/core/network/api/duel/duel.api';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import { HttpError } from '@/core/network/http/httpError';
import type {
  CreateDuelPayload,
  DuelDistancePayload,
  DuelDto,
  DuelMoveDto,
  DuelPingPayload,
  DuelRemovalDto,
  MakeDuelMovePayload,
} from './duel.interface';

const logger = createLogger('DuelRepository');

export class DuelRepository {
  private readonly httpAPI: DuelApi;

  constructor(httpAPI: DuelApi) {
    this.httpAPI = httpAPI;
  }

  async create(payload: CreateDuelPayload): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.create(payload));
  }

  async join(code: string): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.join(code));
  }

  async getActive(): Promise<GamResponse<DuelDto | null> | null> {
    try {
      const response = await this.httpAPI.getActive();
      return { data: response.data?.data ?? null };
    } catch (e) {
      throw this.toApiError(e);
    }
  }

  async getMine(limit?: number): Promise<GamResponse<DuelDto[]> | null> {
    return this.unwrap(() => this.httpAPI.getMine(limit));
  }

  async getByCode(code: string): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.getByCode(code));
  }

  async getDuel(id: string): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.getDuel(id));
  }

  async getMoves(id: string): Promise<GamResponse<DuelMoveDto[]> | null> {
    return this.unwrap(() => this.httpAPI.getMoves(id));
  }

  async ready(id: string, speedMps: number): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.ready(id, speedMps));
  }

  async start(id: string): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.start(id));
  }

  async makeMove(id: string, payload: MakeDuelMovePayload): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.makeMove(id, payload));
  }

  async ping(id: string, payload: DuelPingPayload): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.ping(id, payload));
  }

  async cancel(id: string): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.cancel(id));
  }

  async resign(id: string, payload: DuelDistancePayload): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.resign(id, payload));
  }

  async offerDraw(id: string): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.offerDraw(id));
  }

  async acceptDraw(id: string, payload: DuelDistancePayload): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.acceptDraw(id, payload));
  }

  async declineDraw(id: string): Promise<GamResponse<DuelDto> | null> {
    return this.unwrap(() => this.httpAPI.declineDraw(id));
  }

  async removeMe(id: string): Promise<GamResponse<DuelRemovalDto> | null> {
    return this.unwrap(() => this.httpAPI.removeMe(id));
  }

  private async unwrap<T>(call: () => Promise<{ data?: GamResponse<T> }>): Promise<GamResponse<T> | null> {
    try {
      const response = await call();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      throw this.toApiError(e);
    }
  }

  private toApiError(e: unknown) {
    const httpError = e as HttpError;
    logger.error(`${httpError.error.code} => ${httpError.error.message}`);
    return httpError.error;
  }
}
